import React, { useState, useEffect, useCallback, useRef, ReactNode } from 'react';
import axios from 'axios';
import { ApiService } from '../services/apiService';
import { HealthStatus } from '../types/health';
import { HealthStatusContext, HealthStatusContextValue, AuthState } from './healthStatusContextDef';
import { isAuthenticated, redirectToLogin } from '../services/authTokenService';

type InitializationState = 'initializing' | 'ready' | 'error';

const INITIAL_POLL_INTERVAL = 1500; // 1.5 seconds during initialization
const BACKGROUND_POLL_INTERVAL = 60000; // 1 minute after initialization

interface HealthStatusProviderProps {
  children: ReactNode;
}

export const HealthStatusProvider: React.FC<HealthStatusProviderProps> = ({ children }) => {
  const [state, setState] = useState<InitializationState>('initializing');
  const [healthStatus, setHealthStatus] = useState<HealthStatus | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [initializationMessage, setInitializationMessage] = useState<string>('Initializing system...');
  const [hasCompletedInitialization, setHasCompletedInitialization] = useState(false);
  const [authState, setAuthState] = useState<AuthState>(() =>
    isAuthenticated() ? 'authenticated' : 'checking'
  );
  const pollIntervalRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const apiService = useRef(new ApiService());

  const checkHealth = useCallback(async () => {
    try {
      const health = await apiService.current.getHealthStatus();
      
      setHealthStatus(health);
      setError(null);

      if (health.status === 'healthy') {
        if (authState === 'authenticated') {
          setState('ready');
          setInitializationMessage('System ready');
          if (!hasCompletedInitialization) {
            setHasCompletedInitialization(true);
          }
        } else {
          setInitializationMessage('Checking authentication...');
        }
      } else {
        setState('initializing');
        const unhealthyComponents = Object.entries(health.components)
          .filter(([, component]) => component.status !== 'healthy')
          .map(([name]) => name);
        
        if (unhealthyComponents.length > 0) {
          setInitializationMessage(`Waiting for: ${unhealthyComponents.join(', ')}`);
        } else {
          setInitializationMessage('System initializing...');
        }
      }
    } catch (err: any) {
      // 401 — token invalid/expired, interceptor will redirect
      if (axios.isAxiosError(err) && err.response?.status === 401) {
        if (authState !== 'redirecting') {
          setAuthState('redirecting');
        }
        return;
      }
      if (axios.isAxiosError(err) && err.response?.status === 503) {
        setState('initializing');
        
        try {
          const responseData = err.response?.data;
          if (responseData && typeof responseData === 'object') {
            if (responseData.components) {
              setHealthStatus(responseData as HealthStatus);
              const unhealthyComponents = Object.entries(responseData.components)
                .filter(([, component]: [string, any]) => component.status !== 'healthy')
                .map(([name]) => name);
              setInitializationMessage(`Waiting for: ${unhealthyComponents.join(', ')}`);
            } else if (responseData.message) {
              setInitializationMessage(responseData.message);
            } else {
              setInitializationMessage('Waiting for API to start...');
            }
          } else {
            setInitializationMessage('Waiting for API to start...');
          }
        } catch {
          setInitializationMessage('Waiting for API to start...');
        }
        
        setError(null);
      } else {
        setState('error');
        const message = axios.isAxiosError(err)
          ? (err.response?.data?.message || err.message)
          : (err.message || 'Failed to connect to API');
        setError(message);
        setInitializationMessage('Connection error');
      }
    }
  }, [hasCompletedInitialization, authState]);

  // Check authentication on mount — token-based, no network call needed
  useEffect(() => {
    if (isAuthenticated()) {
      setAuthState('authenticated');
    } else {
      // No token — redirect to login immediately
      setAuthState('redirecting');
      redirectToLogin();
    }
  }, []);

  // When auth becomes authenticated and health is already healthy, mark ready
  useEffect(() => {
    if (authState === 'authenticated' && healthStatus?.status === 'healthy' && !hasCompletedInitialization) {
      setState('ready');
      setInitializationMessage('System ready');
      setHasCompletedInitialization(true);
    }
  }, [authState, healthStatus, hasCompletedInitialization]);

  useEffect(() => {
    checkHealth();

    const interval = hasCompletedInitialization ? BACKGROUND_POLL_INTERVAL : INITIAL_POLL_INTERVAL;
    pollIntervalRef.current = setInterval(checkHealth, interval);

    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, [checkHealth, hasCompletedInitialization]);

  const value: HealthStatusContextValue = {
    healthStatus,
    error,
    isInitializing: state === 'initializing' || authState === 'checking' || authState === 'redirecting',
    initializationMessage,
    authState,
    refreshHealthStatus: checkHealth,
  };

  return (
    <HealthStatusContext.Provider value={value}>
      {children}
    </HealthStatusContext.Provider>
  );
};
